import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import toast from 'react-hot-toast';
import { API_ORIGIN, ScheduleContext, AvailableSlot, FirstMonthResponse } from '../../services/publicService';
import { resolveDurationMinutes, formatSlotTime } from '../../utils/appointmentHelpers';

/**
 * Shared public scheduling UI used by both the patient self-schedule page
 * (/schedule/:patientUuid) and the single-meeting reschedule page
 * (/update-meeting/:uuid). All data access goes through the injected `api`
 * adapter, so the two pages render the identical UI from different endpoints.
 *
 * Screens: 1) select date, 2) select time, 3) booked / existing appointment
 * (manage), 4) cancelled. Book-mode bits are inert when the adapter has no
 * `bookMeeting` (the reschedule page is manage-only).
 */
export interface SchedulingApi {
  getContext: () => Promise<ScheduleContext>;
  getFirstAvailableMonth: (meetingTypeId: number, meetingId?: number) => Promise<FirstMonthResponse>;
  getAvailableSlots: (meetingTypeId: number, fromDate: string, toDate: string, meetingId?: number) => Promise<{ slots: AvailableSlot[] }>;
  bookMeeting?: (data: { meeting_type_id: number; start: string; resource_id?: string }) => Promise<{ ok: boolean }>;
  rescheduleMeeting: (meetingId: number, data: { start: string }) => Promise<{ ok: boolean }>;
  cancelMeeting: (meetingId: number, data: { reason?: string }) => Promise<{ ok: boolean }>;
}

/* ── Design tokens (from the Yoman scheduling mockups) ─────────────── */
const HEADER_TEAL = '#0C576B';
const SELECTION_GREEN = '#17C68C';
const GREEN_HOVER = '#13AA78';
const GREEN_DISABLED = '#A7E6D2';
const RED = '#E07A7A';
const PAGE_BG = '#EEF1F3';
const CARD_BG = '#FFFFFF';
const TEXT_DARK = '#2B2B2B';
const DAY_MUTED = '#C4C9CE';
const NAV_TEAL = '#2E6B78';
const ICON_DARK = '#22303A';

// Weekday headers, Monday-first (Sunday last, rendered in red).
const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const pillSx = {
  borderRadius: 999,
  px: 4,
  py: 1.1,
  minWidth: 150,
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none' as const,
  boxShadow: 'none',
} as const;

const greenPillSx = {
  ...pillSx,
  backgroundColor: SELECTION_GREEN,
  color: '#fff',
  '&:hover': { backgroundColor: GREEN_HOVER, boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: GREEN_DISABLED, color: '#fff' },
} as const;

const redPillSx = {
  ...pillSx,
  backgroundColor: '#F26B7B',
  color: '#fff',
  '&:hover': { backgroundColor: '#E1596A', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#F8B9C1', color: '#fff' },
} as const;

const backBtnSx = {
  border: `1.5px solid ${ICON_DARK}`,
  color: ICON_DARK,
  width: 44,
  height: 44,
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
} as const;

interface ConfirmationDetails {
  typeLabel: string;
  durationMinutes: number | null;
  dateLabel: React.ReactNode;
  timeLabel: string;
  updated: boolean;
}

/* ── date/month helpers ────────────────────────────────────────────── */
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const daysInMonth = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m, 0).getDate();
};
// First weekday of the month, Monday-first (0 = Monday … 6 = Sunday).
const firstWeekdayMon = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return (new Date(y, m - 1, 1).getDay() + 6) % 7;
};
const monthLabel = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
};
const shiftMonth = (month: string, delta: number) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

/** Render "Wednesday, July 17ᵗʰ 2026" with a superscripted ordinal suffix. */
const LongDate: React.FC<{ date: Date; tz?: string }> = ({ date, tz }) => {
  if (isNaN(date.getTime())) return null;
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz });
  const month = date.toLocaleDateString('en-US', { month: 'long', timeZone: tz });
  const day = Number(date.toLocaleDateString('en-US', { day: 'numeric', timeZone: tz }));
  const year = date.toLocaleDateString('en-US', { year: 'numeric', timeZone: tz });
  return (
    <>
      {weekday}, {month} {day}
      <Box component="sup" sx={{ fontSize: '0.6em', mr: 0.5 }}>{ordinal(day)}</Box>
      {year}
    </>
  );
};

const fmtTime = (iso: string | null, tz?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: tz });
};
const dayToDate = (day: string) => new Date(`${day}T00:00:00`);
const absoluteLogo = (logoUrl?: string | null) => (logoUrl ? `${API_ORIGIN}${logoUrl}` : null);
const toTitleCase = (name?: string | null) =>
  (name || '')
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ')
    .trim();
const fmtMins = (m: number | null) => (m && m > 0 ? `${m} Mins.` : '');

/* Appointment-type icon (white calendar + cyan check) for the dark teal header. */
const AppointmentTypeIcon: React.FC = () => (
  <svg width="26" height="28" viewBox="0 0 32 35" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M26.65 3.33997H4.76996C2.52216 3.33997 0.699951 5.16217 0.699951 7.40997V29.29C0.699951 31.5378 2.52216 33.36 4.76996 33.36H26.65C28.8978 33.36 30.72 31.5378 30.72 29.29V7.40997C30.72 5.16217 28.8978 3.33997 26.65 3.33997Z" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M25.4301 0.700012V5.98001" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.97998 0.700012V5.98001" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.84 0.700012V5.98001" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.7 0.700012V5.98001" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20.5699 0.700012V5.98001" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.93005 19.29L12.8 25.16L24.4301 13.53" stroke="#00F3FF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Duration/timer icon (white outline) for the dark teal header. */
const DurationIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M3.20001 7.40002C1.62001 9.77002 0.700012 12.63 0.700012 15.69C0.700012 23.97 7.41 30.68 15.69 30.68C23.97 30.68 30.68 23.97 30.68 15.69C30.68 7.41 23.97 0.700012 15.69 0.700012V6.63" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15.69 15.69L8.75995 8.76001" stroke="#00F3FF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Calendar-with-check icon (dark outline, green check) for the booked date. */
const BookedDateIcon: React.FC = () => (
  <svg width="40" height="44" viewBox="0 0 51 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M42.99 5.71997H8.01001C4.41464 5.71997 1.5 8.63461 1.5 12.23V47.21C1.5 50.8054 4.41464 53.72 8.01001 53.72H42.99C46.5854 53.72 49.5 50.8054 49.5 47.21V12.23C49.5 8.63461 46.5854 5.71997 42.99 5.71997Z" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M41.0599 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.94995 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17.73 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M25.5 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M33.2799 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11.47 31.22L20.86 40.61L39.46 22.02" stroke="#17C68C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Clock icon (dark outline, green hands) for the booked time. */
const BookedTimeIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M25.5 49.5C38.7548 49.5 49.5 38.7548 49.5 25.5C49.5 12.2452 38.7548 1.5 25.5 1.5C12.2452 1.5 1.5 12.2452 1.5 25.5C1.5 38.7548 12.2452 49.5 25.5 49.5Z" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M25.5 10V25.5H35.16" stroke="#17C68C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Calendar icon (dark outline) for the "Appointment is Cancelled" screen. */
const CancelledCalendarIcon: React.FC = () => (
  <svg width="34" height="37" viewBox="0 0 51 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M42.99 5.71997H8.01001C4.41464 5.71997 1.5 8.63458 1.5 12.2299V47.21C1.5 50.8053 4.41464 53.72 8.01001 53.72H42.99C46.5854 53.72 49.5 50.8053 49.5 47.21V12.2299C49.5 8.63458 46.5854 5.71997 42.99 5.71997Z" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M41.0499 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.93994 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17.72 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M25.5 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M33.2799 1.5V9.94" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <g transform="translate(13.8 18.3)">
      <path d="M1.5 1.5L21.97 21.97" stroke="#F26B7B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21.97 1.5L1.5 21.97" stroke="#F26B7B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
);

/* Thumbs-up icon (dark outline) for the "Appointment is Booked" screen. */
const ThumbsUpIcon: React.FC = () => (
  <svg width="34" height="37" viewBox="0 0 51 55" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M11.98 22.36C11.98 22.36 25.87 23.82 29.68 3.01001C29.84 2.14001 30.6 1.5 31.48 1.5C33.7 1.5 37.49 2.53001 37.49 9.54001C37.49 15.33 33.91 19.96 33.91 19.96H44.66C47.56 19.96 51.69 23.32 47.97 28.98C47.97 28.98 49.45 30.91 49.45 33.39C49.45 35.87 47.28 36.97 47.28 36.97C47.28 36.97 48.66 38.49 48.66 41.11C48.66 43.73 45.76 45.25 45.76 45.25C45.76 45.25 46.59 46.9 46.59 48.83C46.59 50.76 44.8 52.55 41.49 52.97C38.18 53.39 25.77 53.52 22.6 51.73C21.82 51.29 21.06 50.86 20.36 50.46C17.85 49.03 15.01 48.28 12.11 48.28H11.98" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.27002 18.92H4.21002C2.71333 18.92 1.5 20.1333 1.5 21.63V50.53C1.5 52.0267 2.71333 53.2401 4.21002 53.2401H9.27002C10.7667 53.2401 11.98 52.0267 11.98 50.53V21.63C11.98 20.1333 10.7667 18.92 9.27002 18.92Z" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Outlined circle marker shown beside the selected date on the time screen. */
const SelectedDateIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 73 73" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M22.22 37.3L32.62 47.7L53.21 27.11" stroke="#17C68C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M36.48 71.46C55.7989 71.46 71.46 55.7989 71.46 36.48C71.46 17.1611 55.7989 1.5 36.48 1.5C17.1611 1.5 1.5 17.1611 1.5 36.48C1.5 55.7989 17.1611 71.46 36.48 71.46Z" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Patient avatar illustration (orange head, teal shoulders) — circular by mask. */
const PatientAvatarSvg: React.FC = () => (
  <svg width="100%" height="100%" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <mask id="patientAvatarMask" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="68" height="68">
      <path d="M33.96 67.92C52.7156 67.92 67.92 52.7156 67.92 33.96C67.92 15.2044 52.7156 0 33.96 0C15.2044 0 0 15.2044 0 33.96C0 52.7156 15.2044 67.92 33.96 67.92Z" fill="white" />
    </mask>
    <g mask="url(#patientAvatarMask)">
      <path d="M42.9585 35.4844C47.926 30.5169 47.926 22.4631 42.9585 17.4956C37.9911 12.5281 29.9372 12.5281 24.9697 17.4956C20.0023 22.4631 20.0023 30.5169 24.9697 35.4844C29.9372 40.4519 37.991 40.4519 42.9585 35.4844Z" fill="#F39136" />
      <path d="M33.96 85.05C45.4586 85.05 54.78 75.7286 54.78 64.23C54.78 52.7315 45.4586 43.41 33.96 43.41C22.4615 43.41 13.14 52.7315 13.14 64.23C13.14 75.7286 22.4615 85.05 33.96 85.05Z" fill="#6EB2B5" />
    </g>
  </svg>
);

/* ── Header (teal banner) — persists across every screen ───────────── */
const HeaderBanner: React.FC<{
  name: string;
  typeLabel: string;
  durationMinutes: number | null;
}> = ({ name, typeLabel, durationMinutes }) => (
  <Box
    sx={{
      backgroundColor: HEADER_TEAL,
      borderRadius: '18px 18px 0 0',
      px: { xs: 2.5, sm: 4 },
      py: { xs: 2.5, sm: 3 },
      color: '#fff',
      boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: '#fff', width: 56, height: 56, p: 0.25 }}>
        <PatientAvatarSvg />
      </Avatar>
      {name && (
        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.4rem', sm: '1.75rem' }, lineHeight: 1.1 }}>
          {name}
        </Typography>
      )}
    </Box>

    {(typeLabel || durationMinutes) && (
      <>
        {/* Mobile only: horizontal rule under the name (design shows a divider on small screens) */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, height: '1px', backgroundColor: 'rgba(255,255,255,0.35)', mt: 2.5 }} />
      <Box
        sx={{
          mt: 2.5,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.5, sm: 2.5 },
          flexWrap: 'nowrap',
        }}
      >
        {typeLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', opacity: 0.95 }}><AppointmentTypeIcon /></Box>
            <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              Appointment type: <Box component="span" sx={{ fontWeight: 700 }}>{typeLabel}</Box>
            </Typography>
          </Box>
        )}
        {typeLabel && durationMinutes ? (
          <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'rgba(255,255,255,0.35)', display: { xs: 'none', sm: 'block' } }} />
        ) : null}
        {durationMinutes ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', opacity: 0.95 }}><DurationIcon /></Box>
            <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              Duration: <Box component="span" sx={{ fontWeight: 700 }}>{fmtMins(durationMinutes)}</Box>
            </Typography>
          </Box>
        ) : null}
      </Box>
      </>
    )}
  </Box>
);

/* ── White content card ────────────────────────────────────────────── */
const ContentCard: React.FC<{ active?: boolean; children: React.ReactNode }> = ({ active, children }) => (
  <Box
    sx={{
      backgroundColor: CARD_BG,
      borderRadius: '0 0 18px 18px',
      p: { xs: 2, sm: 3 },
      boxShadow: active ? '2.55px 2.55px 8.52px 0px #0000004D' : '0 6px 20px rgba(0,0,0,0.06)',
    }}
  >
    {children}
  </Box>
);

/* ── Booked / existing appointment view (screen 3) ─────────────────── */
const BookedView: React.FC<{
  heading: React.ReactNode;
  icon: React.ReactNode;
  dateNode: React.ReactNode;
  timeLabel: string;
  message: React.ReactNode;
  busy: boolean;
  onBack: () => void;
  onCancel: () => void;
  // When set, show an explicit "Reschedule" button (instead of the back-arrow-only
  // affordance) so rescheduling is available on mobile too.
  rescheduleLabel?: string;
}> = ({ heading, icon, dateNode, timeLabel, message, busy, onBack, onCancel, rescheduleLabel }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 2, mb: 3.5 }}>
      {icon}
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.35rem' }, color: ICON_DARK }}>{heading}</Typography>
    </Box>
    <Box sx={{ height: '1px', backgroundColor: '#1F2937', mb: 3 }} />

    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 2.5, sm: 6 }, flexWrap: 'nowrap', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BookedDateIcon />
        <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: ICON_DARK, textAlign: 'left' }}>{dateNode}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BookedTimeIcon />
        <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: ICON_DARK, whiteSpace: 'nowrap' }}>{timeLabel}</Typography>
      </Box>
    </Box>

    <Typography sx={{ color: '#1F2937', fontWeight: 400, fontSize: '0.95rem', lineHeight: 1.6, mt: 6, mb: 3.5 }}>{message}</Typography>

    {rescheduleLabel ? (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Button onClick={onBack} disabled={busy} sx={greenPillSx}>
          {rescheduleLabel}
        </Button>
        <Button
          onClick={onCancel}
          disabled={busy}
          sx={redPillSx}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Cancel Appointment
        </Button>
      </Box>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={onBack} disabled={busy} sx={{ ...backBtnSx, display: { xs: 'none', sm: 'inline-flex' } }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={onCancel}
            disabled={busy}
            sx={redPillSx}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Cancel Appointment
          </Button>
        </Box>
        <Box sx={{ width: 44, display: { xs: 'none', sm: 'block' } }} />
      </Box>
    )}
  </Box>
);

const SchedulingFlow: React.FC<{ api: SchedulingApi; invalidLink?: boolean; blockedMessage?: string }> = ({ api, invalidLink = false, blockedMessage }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ctx, setCtx] = useState<ScheduleContext | null>(null);

  // picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStep, setPickerStep] = useState<'date' | 'time'>('date');
  const [currentMonth, setCurrentMonth] = useState<string>(thisMonth());
  const [monthSlots, setMonthSlots] = useState<AvailableSlot[]>([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationDetails | null>(null);
  const [rebooking, setRebooking] = useState(false);
  const autoOpenRef = useRef(false);
  const pendingActionRef = useRef<'update' | 'cancel' | 'rebook' | null>(null);

  const loadContext = useCallback(async (silent = false) => {
    if (invalidLink) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    try {
      if (!silent) setLoading(true);
      const data = await api.getContext();
      setCtx(data);
      if (!silent) setError(null);
    } catch (err: any) {
      if (!silent) setError(err?.response?.status === 404 ? 'Invalid link' : 'Something went wrong. Please try again later.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [api, invalidLink]);

  useEffect(() => { loadContext(); }, [loadContext]);

  const meetingTypeId = ctx?.mode === 'book'
    ? ctx.next_meeting?.meeting_type_id
    : ctx?.active_meeting?.meeting_type_id || undefined;
  const manageMeetingId = ctx?.mode === 'manage' ? ctx.active_meeting?.id : undefined;

  const openPicker = useCallback(async () => {
    if (invalidLink || !meetingTypeId) return;
    setPickerOpen(true);
    setPickerStep('date');
    setSelectedDay(null);
    setSelectedSlot(null);
    setMonthLoading(true);
    try {
      const res = await api.getFirstAvailableMonth(meetingTypeId, manageMeetingId);
      setCurrentMonth(res.month || thisMonth());
      setMonthSlots(res.slots || []);
    } catch {
      setCurrentMonth(thisMonth());
      setMonthSlots([]);
      toast.error('Could not load availability');
    } finally {
      setMonthLoading(false);
    }
  }, [api, invalidLink, meetingTypeId, manageMeetingId]);

  const loadMonth = useCallback(async (month: string) => {
    if (invalidLink || !meetingTypeId) return;
    setSelectedDay(null);
    setSelectedSlot(null);
    setMonthLoading(true);
    try {
      const from = `${month}-01`;
      const to = `${month}-${String(daysInMonth(month)).padStart(2, '0')}`;
      const res = await api.getAvailableSlots(meetingTypeId, from, to, manageMeetingId);
      setCurrentMonth(month);
      setMonthSlots(res.slots || []);
    } catch {
      setMonthSlots([]);
      toast.error('Could not load availability');
    } finally {
      setMonthLoading(false);
    }
  }, [api, invalidLink, meetingTypeId, manageMeetingId]);

  // In book mode, open the calendar on the first available month automatically.
  useEffect(() => {
    if (ctx?.mode === 'book' && !pickerOpen && !cancelled && !confirmation && !autoOpenRef.current) {
      autoOpenRef.current = true;
      openPicker();
    }
  }, [ctx?.mode, pickerOpen, cancelled, confirmation, openPicker]);

  const handleConfirm = async () => {
    if (invalidLink || !ctx || !selectedSlot) return;
    const slot = selectedSlot;
    const isUpdate = ctx.mode === 'manage';
    setBusy(true);
    try {
      if (ctx.mode === 'book' && ctx.next_meeting && api.bookMeeting) {
        const resourceId = ctx.next_meeting.resource_id || slot.resources?.[0];
        await api.bookMeeting({
          meeting_type_id: ctx.next_meeting.meeting_type_id,
          start: slot.value,
          resource_id: resourceId,
        });
      } else if (ctx.mode === 'manage' && ctx.active_meeting) {
        await api.rescheduleMeeting(ctx.active_meeting.id, { start: slot.value });
      } else {
        return;
      }
      const typeLabel = (ctx.mode === 'book' ? ctx.next_meeting?.name : ctx.active_meeting?.meeting_type_name) || '';
      const durationMinutes = ctx.mode === 'book'
        ? resolveDurationMinutes(ctx.next_meeting?.duration_minutes, ctx.next_meeting?.name)
        : resolveDurationMinutes(ctx.active_meeting?.duration_minutes, ctx.active_meeting?.meeting_type_name);
      setConfirmation({
        typeLabel,
        durationMinutes,
        dateLabel: <LongDate date={dayToDate(slot.date)} />,
        timeLabel: formatSlotTime(slot.display),
        updated: isUpdate,
      });
      setPickerOpen(false);
      setSelectedSlot(null);
      setTimeout(() => loadContext(true), 1500); // DB reconciles via the Yoman webhook (async)
    } catch {
      toast.error('Could not save the appointment. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = useCallback(async () => {
    if (invalidLink || !ctx?.active_meeting) return;
    setBusy(true);
    try {
      await api.cancelMeeting(ctx.active_meeting.id, {});
      setPickerOpen(false);
      setConfirmation(null);
      setCancelled(true);
      setTimeout(() => loadContext(true), 1500);
    } catch {
      toast.error('Could not cancel the appointment. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [api, invalidLink, ctx, loadContext]);

  const handleScheduleAfterCancel = () => {
    if (ctx?.mode === 'book') {
      setCancelled(false);
      openPicker();
      return;
    }
    setRebooking(true);
    pendingActionRef.current = 'rebook';
    loadContext(true);
  };

  const handleConfirmationUpdate = () => {
    setConfirmation(null);
    if (ctx?.mode === 'manage' && ctx.active_meeting) {
      openPicker();
    } else {
      pendingActionRef.current = 'update';
      loadContext();
    }
  };

  const handleConfirmationCancel = () => {
    if (ctx?.mode === 'manage' && ctx.active_meeting) {
      handleCancel();
      return;
    }
    setBusy(true);
    pendingActionRef.current = 'cancel';
    loadContext(true);
  };

  useEffect(() => {
    const action = pendingActionRef.current;
    if (!action) return;
    if (action === 'rebook') {
      if (ctx?.mode === 'book') {
        pendingActionRef.current = null;
        setRebooking(false);
        setCancelled(false);
        openPicker();
      }
      return;
    }
    if (ctx?.mode !== 'manage' || !ctx.active_meeting) return;
    pendingActionRef.current = null;
    if (action === 'update') openPicker();
    else if (action === 'cancel') handleCancel();
  }, [ctx, openPicker, handleCancel]);

  const meetingTypeLabel = ctx?.mode === 'book' ? ctx.next_meeting?.name : ctx?.active_meeting?.meeting_type_name;
  const headerDurationMin = ctx?.mode === 'book'
    ? resolveDurationMinutes(ctx?.next_meeting?.duration_minutes, ctx?.next_meeting?.name)
    : resolveDurationMinutes(ctx?.active_meeting?.duration_minutes, ctx?.active_meeting?.meeting_type_name);
  const logo = absoluteLogo(ctx?.clinic.logo_url);
  const availableDates = new Set(monthSlots.map((s) => s.date));
  const daySlots = selectedDay ? monthSlots.filter((s) => s.date === selectedDay) : [];

  // Keep the header banner stable even after the context reloads to mode 'none'
  // (e.g. immediately after a cancellation).
  const headerRef = useRef<{ name: string; type: string; duration: number | null }>({ name: '', type: '', duration: null });
  const patientName = toTitleCase(ctx?.patient_name);
  if (patientName) headerRef.current.name = patientName;
  if (meetingTypeLabel) headerRef.current.type = meetingTypeLabel;
  if (headerDurationMin) headerRef.current.duration = headerDurationMin;

  /* ── Calendar (screen 1) ─────────────────────────────────────────── */
  const renderCalendar = () => {
    const blanks = firstWeekdayMon(currentMonth);
    const total = daysInMonth(currentMonth);
    const cells: (string | null)[] = [];
    for (let i = 0; i < blanks; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(`${currentMonth}-${String(d).padStart(2, '0')}`);

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 1 }}>
          <IconButton size="small" onClick={() => loadMonth(shiftMonth(currentMonth, -1))} disabled={monthLoading} sx={{ color: NAV_TEAL }}>
            <ArrowLeftIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.35rem' }, color: ICON_DARK, minWidth: { xs: 130, sm: 160 }, textAlign: 'center' }}>
            {monthLabel(currentMonth)}
          </Typography>
          <IconButton size="small" onClick={() => loadMonth(shiftMonth(currentMonth, 1))} disabled={monthLoading} sx={{ color: NAV_TEAL }}>
            <ArrowRightIcon />
          </IconButton>
        </Box>

        {monthLoading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', pb: 1, borderBottom: '1px solid #1F2937' }}>
              {WEEKDAY_LABELS.map((w, i) => (
                <Typography key={i} sx={{ textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: i === 6 ? RED : ICON_DARK }}>
                  {w}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 0, mt: 1 }}>
              {cells.map((date, i) => {
                if (!date) return <Box key={`b${i}`} />;
                const day = Number(date.slice(8, 10));
                const isSunday = dayToDate(date).getDay() === 0;
                const hasSlots = availableDates.has(date);
                const selected = selectedDay === date;
                const color = selected ? '#fff' : isSunday ? RED : hasSlots ? ICON_DARK : DAY_MUTED;
                return (
                  <Box key={date} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 0.5 }}>
                    <Box
                      onClick={() => { if (hasSlots) { setSelectedDay(date); setSelectedSlot(null); } }}
                      sx={{
                        width: { xs: 34, sm: 40 },
                        height: { xs: 34, sm: 40 },
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        fontWeight: selected || hasSlots || isSunday ? 700 : 400,
                        color,
                        cursor: hasSlots ? 'pointer' : 'default',
                        backgroundColor: selected ? SELECTION_GREEN : 'transparent',
                        transition: 'background-color .15s ease',
                        '&:hover': hasSlots && !selected ? { backgroundColor: 'rgba(0,0,0,0.05)' } : undefined,
                      }}
                    >
                      {day}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {availableDates.size === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
                No availability this month — try another month.
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  /* ── Time grid (screen 2) ────────────────────────────────────────── */
  const renderTimeStep = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1.5, sm: 2 }, mb: 2.5 }}>
        <SelectedDateIcon />
        <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.35rem' }, color: ICON_DARK }}>
          {selectedDay && <LongDate date={dayToDate(selectedDay)} />}
        </Typography>
      </Box>
      <Box sx={{ height: '1px', backgroundColor: '#1F2937', mb: 3 }} />

      <Typography sx={{ textAlign: 'center', color: TEXT_DARK, fontSize: '1.05rem', mb: 3 }}>
        What Time Is Best for You?
      </Typography>

      {daySlots.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>No available times.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: { xs: 0.75, sm: 1.5 }, justifyItems: 'center' }}>
          {daySlots.map((s) => {
            const selected = selectedSlot?.value === s.value;
            return (
              <Box
                key={s.value}
                onClick={() => !busy && setSelectedSlot(s)}
                sx={{
                  px: { xs: 1, sm: 2 },
                  py: 1,
                  borderRadius: 999,
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  cursor: busy ? 'default' : 'pointer',
                  color: selected ? '#fff' : TEXT_DARK,
                  backgroundColor: selected ? SELECTION_GREEN : 'transparent',
                  transition: 'background-color .15s ease',
                  '&:hover': !selected ? { backgroundColor: 'rgba(0,0,0,0.05)' } : undefined,
                }}
              >
                {formatSlotTime(s.display)}
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <IconButton onClick={() => { setPickerStep('date'); setSelectedSlot(null); }} disabled={busy} sx={backBtnSx}>
          <ArrowBackIcon />
        </IconButton>
      </Box>
    </Box>
  );

  /* ── Footer pill below the card ──────────────────────────────────── */
  const renderFooterButton = (): React.ReactNode => {
    if (loading || error) return null;

    if (confirmation) return null; // booked view carries its own actions
    if (cancelled) {
      return api.bookMeeting ? (
        <Button
          onClick={handleScheduleAfterCancel}
          disabled={busy || rebooking}
          sx={greenPillSx}
          startIcon={rebooking ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Book New Appointment
        </Button>
      ) : null;
    }

    if (ctx?.mode === 'manage' && ctx.active_meeting && !pickerOpen) return null; // booked view carries its own actions

    if (pickerOpen) {
      if (pickerStep === 'date') {
        return (
          <Button onClick={() => setPickerStep('time')} disabled={busy || !selectedDay} sx={greenPillSx} endIcon={<ChevronRightIcon />}>
            Next
          </Button>
        );
      }
      return (
        <Button
          onClick={handleConfirm}
          disabled={busy || !selectedSlot}
          sx={greenPillSx}
          endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ChevronRightIcon />}
        >
          Next
        </Button>
      );
    }
    return null;
  };

  const footerButton = renderFooterButton();

  return (
    <Box
      dir="ltr"
      sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', backgroundColor: PAGE_BG, px: 2, py: { xs: 1, sm: 1.5 } }}
    >
      <Box sx={{ maxWidth: 620, width: '100%', mt: { xs: 1, sm: 1.5 } }}>
        {logo && (
          <Box
            component="img"
            src={logo}
            alt=""
            sx={{ display: 'block', mx: 'auto', maxWidth: 180, maxHeight: 60, objectFit: 'contain', mb: 2 }}
          />
        )}
        <HeaderBanner
          name={headerRef.current.name}
          typeLabel={headerRef.current.type}
          durationMinutes={headerRef.current.duration}
        />

        <Box sx={{ mt: 1.5 }}>
          <ContentCard active={pickerOpen && pickerStep === 'date'}>
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {!loading && error && <Alert severity="error">{error}</Alert>}

            {/* Screen 3 — Booked confirmation */}
            {!loading && !error && confirmation && (
              <BookedView
                heading="Your Appointment is Booked!"
                icon={<ThumbsUpIcon />}
                dateNode={confirmation.dateLabel}
                timeLabel={confirmation.timeLabel}
                message={
                  <>
                    We will send you a confirmation email shortly with your appointment details and any additional instructions.{' '}
                    <Box component="br" sx={{ display: { xs: 'none', sm: 'inline' } }} />
                    (*Please remember to check your Junk Folder)
                  </>
                }
                busy={busy}
                onBack={handleConfirmationUpdate}
                onCancel={handleConfirmationCancel}
              />
            )}

            {/* Screen 4 — Cancelled */}
            {!loading && !error && !confirmation && cancelled && (
              <Box sx={{ textAlign: 'center', py: 1, minHeight: { xs: 380, sm: 'auto' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2.5 }}>
                  <CancelledCalendarIcon />
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.35rem' }, color: ICON_DARK }}>Your Appointment is Cancelled!</Typography>
                </Box>
                <Box sx={{ height: '1px', backgroundColor: '#1F2937', mb: 5 }} />
                <Typography sx={{ color: RED, fontWeight: 600, mb: 1.5 }}>You have cancelled your appointment.</Typography>
                {api.bookMeeting && (
                  <Typography sx={{ color: TEXT_DARK, fontWeight: 600, lineHeight: 1.6 }}>
                    If you wish to schedule a new appointment,<br />please use the button below.
                  </Typography>
                )}
              </Box>
            )}

            {!loading && !error && !confirmation && !cancelled && ctx && (
              <>
                {ctx.mode === 'none' && (
                  blockedMessage
                    ? <Alert severity="error">{blockedMessage}</Alert>
                    : <Alert severity="info">No upcoming appointment scheduled. Please contact the clinic to arrange one.</Alert>
                )}

                {/* Screen 3 — Existing appointment (manage) */}
                {ctx.mode === 'manage' && ctx.active_meeting && !pickerOpen && (
                  <BookedView
                    heading="Your Appointment Details"
                    icon={<BookedDateIcon />}
                    dateNode={<LongDate date={new Date(ctx.active_meeting.scheduled_at || '')} tz={ctx.clinic.timezone} />}
                    timeLabel={fmtTime(ctx.active_meeting.scheduled_at, ctx.clinic.timezone)}
                    message="You currently have a scheduled appointment. You can reschedule it to another time or cancel it below."
                    busy={busy}
                    onBack={openPicker}
                    onCancel={handleCancel}
                    rescheduleLabel="Reschedule"
                  />
                )}

                {/* Screen 1 (date) / Screen 2 (time) — picker */}
                {pickerOpen && (pickerStep === 'date' ? renderCalendar() : renderTimeStep())}
              </>
            )}
          </ContentCard>
        </Box>

        {footerButton && (
          <Box sx={{ mt: { xs: 3, sm: 2.5 }, display: 'flex', justifyContent: 'center' }}>{footerButton}</Box>
        )}
      </Box>
    </Box>
  );
};

export default SchedulingFlow;
